import { useMemo, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { authClient } from '../lib/auth-client';
import { googleSignInCallbackPath, resolvePostAuthPath } from '../lib/onboarding-flow';
import { AuthCard, Field, inputClass, primaryButtonClass, ErrorBanner, NoticeBanner } from '../components/AuthCard';
import { useAuthCapabilities } from '../hooks/useAuthCapabilities';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const caps = useAuthCapabilities();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const next = useMemo(() => {
    const explicit = params.get('next');
    if (explicit) return resolvePostAuthPath(explicit);
    return (location.state as { from?: string } | null)?.from || '/';
  }, [location.state, params]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const callbackURL = typeof window !== 'undefined' ? `${window.location.origin}${next}` : undefined;
    const { error: err } = await authClient.signIn.email({ email, password, callbackURL });
    setSubmitting(false);
    if (err) {
      setError(err.message || 'Sign in failed. Check your email and password.');
      return;
    }
    navigate(next, { replace: true });
  }

  async function onGoogleWorkspaceSignIn() {
    setError(null);
    setSubmitting(true);
    const callbackURL = typeof window !== 'undefined' ? googleSignInCallbackPath(window.location.origin, next) : next;
    const { error: err } = await authClient.signIn.social({ provider: 'google', callbackURL });
    setSubmitting(false);
    if (err) {
      setError(err.message || 'Google Workspace sign-in failed.');
    }
  }

  return (
    <AuthCard
      title="Sign in"
      subtitle="Sign in to review communication records and follow-ups for your team."
      footer={
        <>
          New to Oracord?{' '}
          <Link to={`/signup${params.get('next') ? `?next=${params.get('next')!}` : ''}`} className="font-semibold text-amber-700 hover:text-amber-800 hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      {params.get('reset') === '1' && (
        <NoticeBanner tone="emerald" className="mb-4">
          Your password was reset. Sign in with the new password.
        </NoticeBanner>
      )}
      {params.get('verified') === '1' && (
        <NoticeBanner tone="emerald" className="mb-4">
          Email verified. You can continue with Oracord.
        </NoticeBanner>
      )}
      <ErrorBanner message={error} />
      {caps?.googleWorkspaceSignInEnabled && (
        <button
          type="button"
          onClick={onGoogleWorkspaceSignIn}
          disabled={submitting}
          className="mb-4 flex w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Continue with Google Workspace
        </button>
      )}
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Email">
          <input
            type="email"
            required
            autoComplete="email"
            autoFocus
            value={email}
            onChange={e => setEmail(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Password">
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className={inputClass}
          />
        </Field>
        <div className="text-right text-sm">
          <Link to={`/forgot-password${params.get('next') ? `?next=${params.get('next')!}` : ''}`} className="font-semibold text-amber-700 hover:text-amber-800 hover:underline">
            Forgot password?
          </Link>
        </div>
        <button type="submit" disabled={submitting} className={primaryButtonClass}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </AuthCard>
  );
}
